import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import { Sidebar } from "./layout/sidebar";

import { Header } from "./layout/header";

import { Footer } from "./layout/footer";

// Import Pages

import Dashboard from "./pages/page";
import LoginPage from "./pages/login";
import Timesheet from "./pages/timesheet/page";

import AddViewLeaves from "./pages/add-view-leaves/page";

import AssignApprover from "./pages/assign-approver/page";

import LeaveManagement from "./pages/leave-management/page";
import LeavePolicies from "./pages/leave-management/leave-policies/page";
import LeaveSummary from "./pages/leave-management/leave-summary/page";
import UserPolicyMapping from "./pages/leave-management/user-policy-mapping/page";

import LeaveRequestApprovals from "./pages/leave-request-approvals/page";

import PendingApprovals from "./pages/pending-approvals/page";

import ManageEngagements from "./pages/manage-engagements/page";

import Reports from "./pages/reports/page";  // or wherever your reports live
import ConfigSettings from "./pages/admin/config-settings/page";
import SystemUsers from "./pages/admin/system-users/page";
import MenuRoleMapping from "./pages/admin/menu-role-mapping/page";
import Templates from "./pages/admin/templates/page";
import ClientLayout from "./pages/client-layout";
import { initializeMsal, ensureMsalLogin, fetchGraphProfile } from "./lib/msalClient";
import { apiClient } from "./lib/apiClient";

function AuthGate({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const sysEnv = (process.env.REACT_APP_SYS_ENV || "").toLowerCase();
  const [hasBootstrappedAuth, setHasBootstrappedAuth] = useState(
    () => sessionStorage.getItem("msalAuthBootstrapped") === "true"
  );
  const [isAuthenticating, setIsAuthenticating] = useState(
    () => (sysEnv === "prod" || sysEnv === "qa") && !hasBootstrappedAuth
  );

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      // Only enforce Azure login in prod/qa
      if (sysEnv === "prod" || sysEnv === "qa") {
        try {
          await initializeMsal();
          const result = await ensureMsalLogin({ scopes: ["openid", "profile", "email", "User.Read"] });
          if (result?.account) {
            // Fetch profile from Graph and persist
            if (!hasBootstrappedAuth) {
              try {
                const profile = await fetchGraphProfile();
                if (profile.displayName) {
                  sessionStorage.setItem("userName", profile.displayName);
                  localStorage.setItem("userName", profile.displayName);
                }
                if (profile.email) {
                  sessionStorage.setItem("userEmail", profile.email);
                  // Notify backend for routing/authentication and get user roles
                  try {
                    const response = await apiClient.post("/auth/login", { email: profile.email });

                    // Store roles from backend response (roles are in response.data.user.roles)
                    if (response.data?.user?.roles) {
                      localStorage.setItem("roles", JSON.stringify(response.data.user.roles));
                      sessionStorage.setItem("roles", JSON.stringify(response.data.user.roles));
                      ////console.log("[auth/login] Stored roles:", response.data.user.roles);
                    }

                    // Optionally store other user data
                    if (response.data?.user?.id) {
                      localStorage.setItem("id", response.data.user.id.toString());
                      sessionStorage.setItem("id", response.data.user.id.toString());
                    }

                    // Store username if available
                    if (response.data?.user?.username) {
                      localStorage.setItem("userName", response.data.user.username);
                      sessionStorage.setItem("userName", response.data.user.username);
                    }

                    // Store email
                    if (response.data?.user?.email) {
                      localStorage.setItem("userEmail", response.data.user.email);
                      sessionStorage.setItem("userEmail", response.data.user.email);
                    }

                  } catch (e) {
                    // eslint-disable-next-line no-console
                    ////console.log("[auth/login] failed:", e);
                  }
                }
                if (profile.photoDataUrl) {
                  sessionStorage.setItem("userPhoto", profile.photoDataUrl);
                }
              } catch (e) {
                // eslint-disable-next-line no-console
                ////console.log("[graph] profile fetch failed", e);
              }
              // On successful login, ensure we land on dashboard when at root
              if (window.location.pathname === "/" || window.location.pathname === "") {
                navigate("/dashboard", { replace: true });
              }
            }
          }
          } finally {
            if (!cancelled) {
              sessionStorage.setItem("msalAuthBootstrapped", "true");
              setHasBootstrappedAuth(true);
              setIsAuthenticating(false);
            }
          }
        } else {
          // In dev mode, no MSAL authentication needed
          setIsAuthenticating(false);
        }
      };
      run();
      return () => {
        cancelled = true;
      };
    }, [navigate, sysEnv, hasBootstrappedAuth]);

  // Show loading screen while authenticating
  if (isAuthenticating && (sysEnv === "prod" || sysEnv === "qa")) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="text-center">
          <div className="inline-block">
            <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
          </div>
          <h2 className="mt-6 text-2xl font-semibold text-gray-900">Signing you in...</h2>
          <p className="mt-2 text-sm text-gray-600">Please wait while we authenticate your account</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function App() {
  const sysEnv = (process.env.REACT_APP_SYS_ENV || "").toLowerCase();

  return (
    <Router>
      <AuthGate>
        <ClientLayout>
          <Routes>
            {/* In dev show login on root; in qa/prod redirect to dashboard */}
            <Route
              path="/"
              element={
                sysEnv === "prod" || sysEnv === "qa"
                  ? <Navigate to="/dashboard" replace />
                  : <LoginPage />
              }
            />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/timesheet" element={<Timesheet />} />
            <Route path="/add-view-leaves" element={<AddViewLeaves />} />
            <Route path="/assign-approver" element={<AssignApprover />} />
            <Route path="/leave-management" element={<Navigate to="/leave-management/leave-policies" replace />} />
            <Route path="/leave-management/leave-policies" element={<LeavePolicies />} />
            <Route path="/leave-management/leave-summary" element={<LeaveSummary />} />
            <Route path="/leave-management/user-policy-mapping" element={<UserPolicyMapping />} />
            <Route path="/leave-request-approvals" element={<LeaveRequestApprovals />} />
            <Route path="/pending-approvals/timesheet" element={<PendingApprovals />} />
            <Route path="/pending-approvals/leave" element={<PendingApprovals />} />
            <Route path="/pending-approvals" element={<Navigate to="/pending-approvals/leave" replace />} />
            <Route path="/manage-engagements" element={<ManageEngagements />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/config-settings" element={<ConfigSettings />} />
            <Route path="/system-users" element={<SystemUsers />} />
            <Route path="/menu-role-mapping" element={<MenuRoleMapping />} />
            <Route path="/templates" element={<Templates />} />
          </Routes>

          {/* Footer */}
          {/* <Footer /> */}
        </ClientLayout>
      </AuthGate>
    </Router>
  );

}

export default App;
